import React, { useState, useEffect, useRef, memo } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Grid,
  InputAdornment,
  Typography,
  Menu,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Apply as ApplyIcon,
} from '@mui/icons-material';

const SearchAndFilterBar = ({
  title,
  onSearch,
  onFilter,
  onCreate,
  onClear,
  searchPlaceholder = "Search...",
  filterOptions = [],
  filterTitle = 'Filters',
  createButtonText = "Create",
  showCreateButton = true,
  searchValue = '', // Controlled search value from parent
  filterValue = {}, // Controlled filter values from parent
  sx = {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState(searchValue || '');
  const [filters, setFilters] = useState(filterValue || {});
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const inputRef = useRef(null);
  const isUserTypingRef = useRef(false);
  const wasFocusedRef = useRef(false);
  const isFilterMenuOpenRef = useRef(false);
  // Loading is removed - SearchAndFilterBar should not re-render during search
  // Loading indicator is handled in the table area only

  // Sync with parent searchValue if provided, but only when user is not actively typing
  // Also skip sync if values are the same to prevent unnecessary re-renders
  useEffect(() => {
    if (!isUserTypingRef.current && searchValue !== undefined && searchValue !== searchTerm) {
      // Only update if the value actually changed
      setSearchTerm(searchValue);
    }
  }, [searchValue]);

  // Sync with parent filterValue if provided
  // Only sync if filterValue is explicitly provided (not undefined) and is different
  // Don't sync while filter menu is open to prevent resetting user's selections
  useEffect(() => {
    if (filterValue !== undefined && !isFilterMenuOpenRef.current) {
      const currentFiltersStr = JSON.stringify(filters || {});
      const newFiltersStr = JSON.stringify(filterValue || {});
      if (currentFiltersStr !== newFiltersStr) {
        setFilters(filterValue || {});
      }
    }
  }, [filterValue]);

  // Restore focus after re-render if it was focused before
  useEffect(() => {
    if (wasFocusedRef.current && inputRef.current) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Place cursor at end of text
          const length = inputRef.current.value.length;
          inputRef.current.setSelectionRange(length, length);
        }
      }, 0);
    }
  });

  const handleSearchChange = (event) => {
    const value = event.target.value;
    isUserTypingRef.current = true;
    setSearchTerm(value);
    wasFocusedRef.current = true; // Track that user is interacting
    // Immediately notify parent of search change
    if (onSearch) {
      onSearch(value);
    }
    // Reset typing flag after a short delay
    setTimeout(() => {
      isUserTypingRef.current = false;
    }, 1000);
  };

  const handleFocus = () => {
    wasFocusedRef.current = true;
  };

  const handleBlur = () => {
    // Only clear focus tracking if user explicitly clicked away
    // Don't clear on programmatic blur
    setTimeout(() => {
      if (document.activeElement !== inputRef.current) {
        wasFocusedRef.current = false;
      }
    }, 100);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchTerm);
    }
  };

  const handleFilter = () => {
    if (onFilter) {
      onFilter(filters);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setFilters({});
    if (onClear) {
      onClear();
    }
  };

  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
    isFilterMenuOpenRef.current = true;
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
    isFilterMenuOpenRef.current = false;
  };

  const handleApplyFromMenu = () => {
    handleFilter();
    handleFilterMenuClose();
  };
  const handleCancelFromMenu = () => {
    // Reset filters to the last applied values from parent
    if (filterValue !== undefined) {
      setFilters(filterValue || {});
    }
    handleFilterMenuClose();
  };

  // Search is now triggered on typing via debounce, so Enter key handler is not needed

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value && value !== '').length;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0 || searchTerm.trim() !== '';

  // Memoize the component to prevent unnecessary re-renders
  return (
    <Box sx={{ mb: { xs: 2, md: 3 }, ...sx }}>
      {/* Heading */}
      {title && (
        <Typography 
          variant="h6" 
          component="h1" 
          sx={{ 
            mb: { xs: 1.5, md: 2 }, 
            fontWeight: 700, 
            color: 'text.primary',
            fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
            letterSpacing: '0.01em'
          }}
        >
          {title}
        </Typography>
      )}
      
      {/* Search and Filter Bar */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: { xs: 1, sm: 2 }, 
          borderRadius: 2,
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: { xs: 0.75, sm: 2 }, 
          alignItems: 'center',
          width: '100%',
          overflow: 'hidden'
        }}>
          {/* Search Field */}
          <Box sx={{ 
            flex: { xs: '1 1 0', sm: '1 1 auto' },
            minWidth: { xs: 0, sm: 0 },
            maxWidth: '100%',
            width: { xs: 0, sm: '100%' }
          }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              placeholder={isMobile ? "Search..." : searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              size="small"
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchTerm('');
                        if (onSearch) {
                          onSearch('');
                        }
                      }}
                      edge="end"
                      sx={{ padding: '4px' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Filter Dropdown Button */}
          <Box sx={{ 
            flexShrink: 0,
            flexGrow: 0
          }}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={handleFilterMenuOpen}
              size="small"
              sx={{ 
                minWidth: { xs: 36, sm: 'auto' },
                maxWidth: { xs: 36, sm: 'none' },
                px: { xs: 0.5, sm: 2 },
                '& .MuiButton-startIcon': {
                  marginRight: { xs: 0, sm: 1 },
                  marginLeft: { xs: 0, sm: -1 }
                }
              }}
            >
              {!isMobile && 'Filters'}
              {getActiveFiltersCount() > 0 && (
                <Chip
                  label={getActiveFiltersCount()}
                  size="small"
                  color="primary"
                  sx={{ 
                    ml: { xs: 0.5, sm: 1 }, 
                    height: 16, 
                    fontSize: '0.65rem',
                    minWidth: 16,
                    display: { xs: 'inline-flex', sm: 'inline-flex' }
                  }}
                />
              )}
            </Button>
          </Box>

          {/* Create Button - Always Rightmost */}
          <Box sx={{ 
            flexShrink: 0,
            flexGrow: 0
          }}>
            {showCreateButton && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onCreate}
                size="small"
                sx={{ 
                  minWidth: { xs: 36, sm: 120 },
                  maxWidth: { xs: 36, sm: 'none' },
                  px: { xs: 0.5, sm: 2 },
                  '& .MuiButton-startIcon': {
                    marginRight: { xs: 0, sm: 1 },
                    marginLeft: { xs: 0, sm: -1 }
                  }
                }}
              >
                {!isMobile && createButtonText}
              </Button>
            )}
          </Box>

          {/* Filter options rendered inside dropdown menu */}
        </Box>
      </Paper>

      {/* Filter Dropdown Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterMenuClose}
        PaperProps={{
          sx: {
            minWidth: { xs: 280, sm: 320 },
            maxWidth: { xs: '90vw', sm: 400 },
            mt: 1,
            p: { xs: 1.5, sm: 2 },
          }
        }}
      >
        <Box sx={{ px: 1, py: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{filterTitle}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            {filterOptions.map((option) => (
              <FormControl key={option.field} size="small">
                <InputLabel>{option.label}</InputLabel>
                <Select
                  value={filters[option.field] || ''}
                  onChange={handleFilterChange(option.field)}
                  label={option.label}
                >
                  <MenuItem value="">
                    <em>All {option.label}</em>
                  </MenuItem>
                  {option.options.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 2 }}>
            <Button variant="text" onClick={handleCancelFromMenu}>Cancel</Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {hasActiveFilters && (
                <Button variant="outlined" onClick={handleClear}>Clear</Button>
              )}
              <Button variant="contained" onClick={handleApplyFromMenu}>Apply</Button>
            </Box>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

// Memoize component to prevent unnecessary re-renders when only table data changes
// Note: We intentionally don't compare 'loading' prop to allow loading indicator updates
// without causing full component re-renders. The loading state is handled internally via refs/state.
export default memo(SearchAndFilterBar, (prevProps, nextProps) => {
  // Only re-render if these critical props actually change
  // Loading prop is excluded so SearchAndFilterBar doesn't re-render during search
  // Note: We intentionally don't compare function props (onSearch, onFilter, etc.) 
  // as they should be stable references from useCallback in parent components
  return (
    prevProps.title === nextProps.title &&
    prevProps.searchPlaceholder === nextProps.searchPlaceholder &&
    prevProps.searchValue === nextProps.searchValue &&
    prevProps.createButtonText === nextProps.createButtonText &&
    prevProps.showCreateButton === nextProps.showCreateButton &&
    JSON.stringify(prevProps.filterOptions) === JSON.stringify(nextProps.filterOptions)
  );
});
